import cls from "@fvilers/cls";
import {
  ComponentPropsWithoutRef,
  use,
  useCallback,
  useEffect,
  useRef,
  useState,
} from "react";
import { createPortal } from "react-dom";
import { getFocusableElements } from "../../helpers/focus";
import { handleListNavigation } from "../../helpers/listNavigation";
import { useRelativePosition } from "../../hooks/useRelativePosition";
import { Align, Side } from "../../hooks/useRelativePosition/types";
import { PopoverContext } from "./contexts/PopoverContext";
import styles from "./Popover.module.css";

type PopoverContentProps = {
  align?: Align;
  alignOffset?: number;
  children: React.ReactNode;
  keyboardNavigation?: boolean;
  offset?: number;
  side?: Side;
  viewportMargin?: number;
} & ComponentPropsWithoutRef<"div">;

export function PopoverContent({
  align = "center",
  alignOffset = 0,
  children,
  className,
  keyboardNavigation,
  offset = 8,
  onKeyDown,
  side = "bottom",
  style,
  viewportMargin = 8,
  ...props
}: PopoverContentProps) {
  const contentRef = useRef<HTMLDivElement>(null);
  const wasOpenRef = useRef(false);
  const { anchorRef, open, popoverId, setOpen, triggerRef } =
    use(PopoverContext);
  const [localOpen, setLocalOpen] = useState(open);

  const closePopover = useCallback(() => {
    setOpen(false);
    setLocalOpen(false);
  }, [setOpen]);

  const { position } = useRelativePosition({
    align,
    alignOffset,
    anchorRef: anchorRef ?? triggerRef,
    close: closePopover,
    contentRef,
    offset,
    open,
    side,
    viewportMargin,
  });

  useEffect(() => {
    if (open) {
      const content = contentRef.current;
      content?.showPopover();
      if (keyboardNavigation && content) {
        getFocusableElements({ element: content })[0]?.focus();
      }
      // Prevent scrolling of the background content when popover is open
      document.documentElement.style.overflow = "hidden";
    } else {
      document.documentElement.style.overflow = "";
    }

    // Prevent interaction with background content when popover is open
    const bodyChildren = Array.from(document.body.children);
    for (const child of bodyChildren) {
      if (child !== contentRef.current) {
        if (child instanceof HTMLElement) {
          child.inert = open;
        }
      }
    }

    // Restore focus once the trigger is no longer inert, unless the focus has
    // already moved elsewhere
    if (!open && wasOpenRef.current) {
      const { activeElement } = document;
      const focusIsLost =
        !activeElement ||
        activeElement === document.body ||
        !!contentRef.current?.contains(activeElement);
      if (focusIsLost) triggerRef.current?.focus();
    }
    wasOpenRef.current = open;

    return () => {
      document.documentElement.style.overflow = "";
      for (const child of bodyChildren) {
        if (child instanceof HTMLElement) {
          child.inert = false;
        }
      }
    };
  }, [keyboardNavigation, open, triggerRef]);

  if (open && !localOpen) setLocalOpen(true);
  if (!open && localOpen) setTimeout(() => setLocalOpen(false), 100);

  if (!localOpen) return null;

  return createPortal(
    <div
      className={cls(styles.PopoverContent, className)}
      data-state={open ? "open" : "closed"}
      id={popoverId}
      onKeyDown={(event) => {
        onKeyDown?.(event);
        if (keyboardNavigation) handleListNavigation(event);
      }}
      onToggle={(e) => {
        if (!e.currentTarget.matches(":popover-open")) {
          closePopover();
        }
      }}
      popover="auto"
      ref={contentRef}
      style={
        {
          ...(style ?? {}),
          ...position,
        } as React.CSSProperties
      }
      {...props}
    >
      {children}
    </div>,
    document.body,
  );
}
